/* eslint-disable arrow-body-style, object-curly-newline, import/no-extraneous-dependencies,
no-nested-ternary */
/* global globalThis, React */
import { useState, useEffect, useRef } from 'react'
import { Button, ButtonGroup, InputAdornment, TextField } from '@mui/material'
import { withStyles } from '@mui/styles'
import PlatformEvent from 'lib/util/event'
import { useMemoRef } from '@platform/react/hook'
import { ClearAdornment } from 'ui/Element/Field/Text'
import DecoratedText from 'ui/Element/Text/Decorated'
import Icon from 'ui/Element/Icon'

const buttonStyles = theme => ({
  minWidth: [0, '!important'],
  minHeight: 0,
  width: 'auto',
  padding: theme.spacing(0, 1),
  margin: theme.spacing(-1, 0),
  borderRadius: 0,
  '&.Mui-disabled': {
    opacity: 0.25,
  },
})

const styles = theme => ({
  root: {
    '& #clear': {
      display: 'none',
    },
    '& #clear:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&:hover #clear': {
      display: 'inline-flex',
    },
    '& input:focus + div > #clear, & input:active + div > #clear': {
      display: 'inline-flex',
    },
  },
  input: {
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    MozAppearance: 'none',
  },
  fullHeight: {
    height: ['100%', '!important'],
  },
  buttonsAdornment: {
    cursor: 'default',
  },
  buttonGroup: {
    borderRadius: '50%',
    overflow: 'hidden',
    rowGap: 1,
  },
  incrementButton: {
    paddingTop: 0,
    paddingBottom: 0,
    ...buttonStyles(theme),
  },
  decrementButton: {
    paddingTop: 0,
    paddingBottom: 0,
    ...buttonStyles(theme),
  },
  unitAdornment: {
    userSelect: 'none',
    cursor: 'default',
  },
})

/**
 * Unit adornment.
 *
 * @param {Object} classes          style definitions
 * @param {string} label            text content
 * @param {'start'|'end'} position  placement inside the field
 * @returns {null|JSX.Element}
 */
const UnitAdornment = ({ classes, label, position }) => (!label ? null : (
  <InputAdornment
    key={'unit'}
    position={position || 'end'}
    className={classes.unitAdornment}
  >
    <DecoratedText
      value={label}
    />
  </InputAdornment>
))

/**
 * Increase & decrease buttons adornment that allow key repetition by holding.
 *
 * @param {Object} classes    style definitions
 * @param {Function} onClick  called whenever a button is clicked
 * @param {Object} disabled   an object containing `increment` and `decrement` boolean properties
 *                            indicating which buttons are disabled
 * @returns {null|JSX.Element}
 */
const ButtonsAdornment = ({ classes, onClick, disabled }) => {
  const intervalRef = useRef(null)
  const timeoutRef = useRef(null)

  const handleClickStart = amount => (event) => {
    onClick?.(new PlatformEvent(event, { value: amount }))

    globalThis.addEventListener('mouseup', () => {
      clearTimeout(timeoutRef.current)
      clearInterval(intervalRef.current)
      timeoutRef.current = null
      intervalRef.current = null
    }, { once: true })

    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        onClick?.(new PlatformEvent(event, { value: amount }))
      }, 200)
    }, 500)
  }

  return (
    <InputAdornment
      key={'buttons'}
      position={'end'}
      className={classes.buttonsAdornment}
    >
      <ButtonGroup
        orientation={'vertical'}
        className={classes.buttonGroup}
      >
        <Button
          key={'increment'}
          onMouseDown={handleClickStart(1)}
          className={classes.incrementButton}
          disabled={disabled.increment}
          variant={'text'}
          disableRipple={true}
        >
          <Icon
            name={'caret-up'}
            iconPadding={0}
            size={20}
          />
        </Button>
        <Button
          key={'decrement'}
          onMouseDown={handleClickStart(-1)}
          className={classes.decrementButton}
          disabled={disabled.decrement}
          variant={'text'}
          disableRipple={true}
        >
          <Icon
            name={'caret-down'}
            iconPadding={0}
            size={20}
          />
        </Button>
      </ButtonGroup>
    </InputAdornment>
  )
}

/**
 * Number Field Element
 *
 * Wrapper around a number {@link TextField} intended to adapt the element's functionality to the
 * application's requirements and enable its use and management through its configuration files.
 *
 * @see https://v5.mui.com/material-ui/api/text-field/
 *
 * @param {React.Ref<any>} forwardedRef                 forwarded reference to the root element
 * @param {Object} events                               events for the input field
 * @param {Object} classes                              the component's style classes
 * @param {string} [helperText]                         helper text to be displayed
 * @param {boolean} [focused]                           whether or not the input should be focused
 * @param {Object} props                                additional props
 * @param {string} [props.value]                        input value
 * @param {string} [props.variant]                      the input style variant
 * @param {boolean} [props.disabled]                    whether to show the element in disabled
 *                                                      state and prevent the user from using it
 * @param {boolean} [props.clearable]                   whether to display the clear button
 * @param {number} [props.step]                         step button increase/decrease amount
 * @param {boolean} [props.min]                         minimum value allowed by the step buttons
 * @param {boolean} [props.max]                         maximum value allowed by the step buttons
 * @param {boolean} [props.unitStart]                   unit displayed at the start of the field
 * @param {boolean} [props.unitEnd]                     unit displayed at the end of the field
 * @param {HTMLInputTypeAttribute|string} [props.type]  type of the input field
 * @returns {*}
 */
const NumberField = ({ forwardedRef, events, classes, helperText, focused, ...props }) => {
  const {
    disabled,
    clearable,
    step,
    min,
    max,
    unitStart,
    unitEnd,
    ...restProps
  } = props
  const { onChange } = events || {}

  const [value, setValue] = useState(props.value ?? '')
  const inputRef = restProps.inputRef || useRef()
  const firstRenderRef = useRef(true)

  const decimalsCount = useRef(step ? step.toString().split('.')[1]?.length || 0 : 0)

  const clear = () => {
    setValue('')
    inputRef.current?.focus()
  }

  // Handling input events
  const handleChange = (event) => {
    const rawValue = event.target.value
    const newValue = rawValue === '' ? rawValue : parseFloat(rawValue)
    setValue(newValue)
  }

  const handleStep = (event) => {
    inputRef.current?.focus()

    setValue((prevValue) => {
      const floatValue = prevValue ? parseFloat(prevValue) : 0
      const stepValue = step * event.detail.value
      const newValue = parseFloat((floatValue + stepValue).toFixed(decimalsCount.current))

      return ((newValue > max) && max) || ((newValue < min) && min) || newValue
    })
  }

  useEffect(() => {
    // The state has already been initialized from props, and
    // we don't want to call onChange during the first render
    !firstRenderRef.current
      && setValue(props.value ?? '')
    firstRenderRef.current = false
  }, [props.value])

  // Note: repeatedly triggering onChange inside the setValue callback in handleStep (which is
  // required to properly obtain the previous value), may cause other elements to be rerendered,
  // which is a problem for React. Because of that, we need the following useEffect to trigger the
  // onChange handler
  useEffect(() => {
    value !== props.value
      && onChange?.(new PlatformEvent('change', { value: value === '' ? null : value }))
  }, [value])

  return (
    <TextField
      {...restProps}
      ref={forwardedRef}
      inputRef={inputRef}
      value={value}
      className={classes.root}
      fullWidth
      type={'number'}
      autoFocus={focused}
      onChange={handleChange}
      variant={props.variant || 'standard'}
      disabled={disabled}
      helperText={helperText || '\u00A0'}
      FormHelperTextProps={{
        sx: { maxWidth: 'fit-content' },
      }}
      inputProps={{
        ...restProps.inputProps,
        type: 'number',
        step: 'any',
        className: classes.input,
      }}
      InputProps={{
        ...restProps.InputProps,
        startAdornment: unitStart && (
          <UnitAdornment
            label={unitStart}
            position={'start'}
            classes={classes}
          />
        ),
        endAdornment: <>
          {clearable && !disabled && (
            <ClearAdornment
              show={!!value || value === 0}
              classes={classes}
              onClick={clear}
            />
          )}
          {unitEnd && (
            <UnitAdornment
              label={unitEnd}
              position={'end'}
              classes={classes}
            />
          )}
          {step && (
            <ButtonsAdornment
              classes={classes}
              onClick={handleStep}
              disabled={{
                increment: value >= max,
                decrement: value <= min,
              }}
            />
          )}
          {restProps.InputProps?.endAdornment}
        </>,
      }}
    />
  )
}

export default useMemoRef(withStyles(styles)(NumberField), props => [
  props.value,
  props.label,
  props.placeholder,
  props.helperText,
  props.disabled,
  props.required,
  props.error,
])
